import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useFeatureFlag } from '../_hooks/useFeatureFlag';
import { addClient as adviserAreaAddClientAction } from '../AdviserArea/AdviserAreaActions';
import { queryKeys as clientSearchQKs } from '../ClientSearch/api/queryKeys';
import { Alert } from '../CommonComponents/OPL/Alert/Alert';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Input } from '../CommonComponents/OPL/Input/Input';
import { Modal } from '../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../CommonComponents/OPL/Modal/ModalHeader';
import { Feature } from '../CommonSelectors/CommonArea.types';
import { FormValues, RedtailClientOption, validation } from './AddClientModal.meta';
import { Client, useAddClientMutation } from './api/useAddClientMutation';
import { RedtailImport } from './components/RedtailImport/RedtailImport';

interface Props {
  clientToClone?: Client;
  onClose(): void;
}

export const AddClientModal: React.FC<Props> = ({ clientToClone, onClose }) => {
  const [
    selectedRetailClient,
    setSelectedRedtailClient,
  ] = React.useState<RedtailClientOption | null>(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      firstName: clientToClone ? clientToClone.firstName : '',
      lastName: clientToClone ? clientToClone.lastName : '',
    },
    resolver: yupResolver(validation),
    context: { hasRedtailImport: selectedRetailClient !== null },
  });

  const {
    mutateAsync: addClient,
    isLoading: isAddingClient,
    error: addClientError,
  } = useAddClientMutation();

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const showRedtailStandalone = useFeatureFlag(Feature.Redtail);

  const handleAddClient = async ({ firstName, lastName }: FormValues) => {
    if (selectedRetailClient) {
      if (selectedRetailClient.OnboardingEndpoint) {
        window.open(selectedRetailClient.OnboardingEndpoint, '_blank');
      } else {
        ADV.util.Router.go2('ConsumerOverview', { clientId: selectedRetailClient.value });
      }

      return onClose();
    }

    try {
      const { clientId, newConsumerId } = await addClient({
        id: clientToClone?.id,
        firstName,
        lastName,
      });

      ADV.util.clearDashboardCache();

      dispatch(adviserAreaAddClientAction());

      queryClient.invalidateQueries(clientSearchQKs.clientList());

      ADV.util.Router.go2('ConsumerOverview', {
        clientId: clientToClone ? newConsumerId : clientId,
      });

      return onClose();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Modal isOpen onRequestClose={onClose} aria={{ labelledby: 'add-client-modal-heading' }}>
      <ModalHeader id="add-client-modal-heading">
        {clientToClone
          ? `Clone ${clientToClone.firstName} ${clientToClone.lastName}`
          : 'Add a Client'}
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <form onSubmit={handleSubmit(handleAddClient)}>
        {addClientError && (
          <ModalBody>
            <Alert message={addClientError.message} variant="error" />
          </ModalBody>
        )}

        <ModalBody>
          <Box
            display="grid"
            css={({ theme }) => ({
              gridTemplateColumns: '1fr 1fr',
              gridGap: theme.space['16'],
            })}
          >
            <Input
              {...register('firstName')}
              type="text"
              label="First Name"
              formVariant={errors.firstName ? 'error' : undefined}
              helperText={errors.firstName?.message}
              maxLength={50}
            />

            <Input {...register('lastName')} type="text" label="Last Name" maxLength={50} />
          </Box>

          {showRedtailStandalone && <RedtailImport onSelect={setSelectedRedtailClient} />}
        </ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button type="submit" isLoading={isAddingClient} ml="12">
            {clientToClone ? 'Clone' : 'Add'} Client
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
